<script setup lang="ts">
import { PhotoIcon } from "@heroicons/vue/24/outline";
import { twMerge } from "tailwind-merge";
import { useTemplateRef } from "vue";

import { useUser } from "@/composables/api/useUser";

import TrashIcon from "@/components/icons/TrashIcon.vue";
import Button from "@/components/ui/Button.vue";

const model = defineModel<File[]>({ required: true });
const inputRef = useTemplateRef<HTMLInputElement>("fileInput");
const { data: user } = useUser();

function handleFileSelect(e: Event) {
  const target = e.target as HTMLInputElement;
  const files = target.files;

  if (files && files.length > 0) {
    model.value = [...model.value, ...Array.from(files)];

    // Reset the file input value so the same file can be selected again
    if (target) {
      target.value = "";
    }
  }
}

function removeFile(index: number) {
  model.value = model.value.filter((_, i) => i !== index);
}

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} o`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} Ko`;
  }

  if (size < 1024 * 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(1)} Mo`;
  }

  return "Too large";
}

defineExpose({ inputRef });
</script>

<template>
  <div
    class="border-galbani bg-galbani/10 flex w-full flex-col items-center justify-center gap-4 rounded-[20px] border-2 border-dashed px-4 py-6"
  >
    <input
      ref="fileInput"
      type="file"
      accept=".pdf,.jpg,.jpeg,.png,.heic"
      class="hidden"
      :max="10 * 1024 * 1024"
      multiple
      @change="handleFileSelect"
    />

    <template v-if="model.length === 0">
      <p
        :class="
          twMerge(
            'text-galbani text-center text-lg font-medium',
            user?.current_mechanic === 'lactel' && 'text-lactel-blue',
          )
        "
      >
        {{ $t("dialog.uploadFile.pickFile") }}
      </p>

      <p class="text-secondary text-center text-sm">
        {{ $t("dialog.uploadFile.allowedFiles") }}
      </p>

      <Button variant="primary" @click="inputRef?.click()">
        {{ $t("dialog.uploadFile.pickFileButton") }}
      </Button>

      <p class="text-secondary text-center text-sm">
        {{ $t("dialog.uploadFile.warning") }}
      </p>
    </template>

    <template v-else>
      <div class="flex w-full flex-col items-center gap-5">
        <div
          v-for="(file, index) in model"
          :key="index"
          class="flex w-full max-w-64 items-center gap-3"
        >
          <PhotoIcon
            :class="
              twMerge(
                'text-galbani size-8 shrink-0',
                user?.current_mechanic === 'lactel' && 'text-lactel-blue',
              )
            "
          />

          <div class="flex w-full flex-col gap-1">
            <p
              title="{{ file.name }}"
              :class="
                twMerge(
                  'text-galbani truncate text-base font-medium',
                  user?.current_mechanic === 'lactel' && 'text-lactel-blue',
                )
              "
            >
              {{ file.name }}
            </p>

            <p class="text-secondary text-sm">{{ formatFileSize(file.size) }}</p>
          </div>

          <button
            type="button"
            class="text-secondary shrink-0 cursor-pointer"
            @click="() => removeFile(index)"
          >
            <TrashIcon class="size-5" />
          </button>
        </div>
      </div>

      <Button variant="primary" @click="inputRef?.click()">
        {{ $t("dialog.uploadFile.addMoreFiles") }}
      </Button>
    </template>
  </div>
</template>
